import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withStyles } from '@mui/styles';
import {
  Autocomplete, Avatar, Box, Button, Divider, IconButton, Menu, MenuItem, Paper, Popover, Skeleton, TextField, Tooltip, Typography
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanguageIcon from '@mui/icons-material/Language';
import DialpadIcon from '@mui/icons-material/Dialpad';
import PeopleAltIcon from '@mui/icons-material/PeopleAltOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import CallMadeIcon from '@mui/icons-material/CallMade';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { Styles } from '../../styles/material-styles';
import { vt, presenceColors, primaryBtnSx } from '../../styles/voip-theme';
import moment from 'moment';
import voipService from '../../services/voip.service';

// número exibido conforme direção; formatação de data
const callNumber = (c) => (c.direction === 'inbound' ? c.fromNumber : c.toNumber) || '—';
const fmtWhen = (iso) => (iso ? moment(iso).format('DD/MM HH:mm') : '');
// Heurística de telefone válido (>=10 dígitos): habilita/desabilita "Ligar".
const onlyDigits = (s) => String(s || '').replace(/\D/g, '');
// Telefone válido = 10–13 dígitos (BR máx 13 com DDI). Exclui IDs da Meta (Instagram/Messenger, 15-17 dígitos).
const isValidPhone = (s) => { const n = onlyDigits(s).length; return n >= 10 && n <= 13; };
// Front só limpa os dígitos; o formato específico do provedor (Nvoip = nacional, sem 55)
// é responsabilidade do driver no backend.
const toDialNumber = (s) => onlyDigits(s);

// =====================================================================
// VoipSoftphoneWidget — softphone flutuante GLOBAL (padrão Aircall/Dialpad).
// Montado 1x no App.js (fora das <Routes>) → persiste em toda navegação.
// Outbound CLICK-TO-CALL = REAL (origina via API + persiste tb_voip_call).
// WebRTC e inbound ainda em preview (sem engine SIP / sem eventos de chamada).
// Contatos/Recentes ainda mockados (dados entram em fatia futura).
// =====================================================================

const fmtClock = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

const PRESENCE = {
  available: { key: 'voip.sp.presence.available', def: 'Disponível' },
  busy: { key: 'voip.sp.presence.busy', def: 'Ocupado' },
  offline: { key: 'voip.sp.presence.offline', def: 'Offline' },
};

const KEYS = [
  { d: '1', s: '' }, { d: '2', s: 'ABC' }, { d: '3', s: 'DEF' },
  { d: '4', s: 'GHI' }, { d: '5', s: 'JKL' }, { d: '6', s: 'MNO' },
  { d: '7', s: 'PQRS' }, { d: '8', s: 'TUV' }, { d: '9', s: 'WXYZ' },
  { d: '*', s: '' }, { d: '0', s: '+' }, { d: '#', s: '' },
];

// Modos de chamada suportados (real: provider.supported_modalities).
const MODES = {
  click_to_call: { key: 'voip.sp.mode.ctc', def: 'Click-to-call', descKey: 'voip.sp.mode.ctc.desc', desc: 'Seu softphone toca primeiro; áudio fora do navegador', Icon: HeadsetMicIcon },
  webrtc: { key: 'voip.sp.mode.webrtc', def: 'WebRTC', descKey: 'voip.sp.mode.webrtc.desc', desc: 'Ligue e fale direto no navegador', Icon: LanguageIcon },
};


class VoipSoftphoneWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      presence: 'available',
      presenceAnchor: null,
      settingsAnchor: null,
      modality: 'click_to_call',
      // gate/setup reais
      voipEnabled: null,   // null=carregando | true | false
      loadingSetup: true,
      extensions: [],      // ramais do agente logado [{id, ext, name, configId}]
      ramal: null,
      // rascunho do popover de config (só aplica no "Aplicar")
      draftRamal: null,
      draftModality: 'click_to_call',
      call: null,
      ctxOpen: false,
      dialNumber: '',
      dialTab: 'keypad',     // keypad | contacts | recents
      // contatos (busca real)
      contactQuery: '',
      contactResults: [],
      loadingContacts: false,
      contactSearched: false,
      // recentes (real)
      recents: [],
      loadingRecents: false,
      recentsLoaded: false,
      // inbound recebida (vincular/criar)
      linkCandidates: [],
      loadingCandidates: false,
      linking: false,
    };
    this._seq = [];
    this._timer = null;
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    window.__mexxVoip = { enabled: false, hasRamal: false, ramal: null };
    window.addEventListener('voip:dial', this._onExternalDial);
    window.addEventListener('voip:incoming-call', this._onIncomingCall);
    window.addEventListener('voip:call-handled', this._onCallHandled);
    const user = this.props.userSession && this.props.userSession.user;
    const isClient = user && (user.userrole || []).some((r) => r.role && r.role.roletype == 3);
    if (user && !isClient) this.loadSetup();
    else this.setState({ voipEnabled: false, loadingSetup: false });
  }
  componentWillUnmount() { this._mounted = false; window.removeEventListener('voip:dial', this._onExternalDial); window.removeEventListener('voip:incoming-call', this._onIncomingCall); window.removeEventListener('voip:call-handled', this._onCallHandled); this._clearSeq(); }

  // Publica o estado do VOIP (habilitado / tem ramal / ramal atual) para outros locais (aba Telefonia,
  // Acompanhamento) decidirem se mostram o atalho de ligar e qual provedor exibir na confirmação.
  componentDidUpdate(prevProps, prevState) {
    const rid = (r) => (r ? r.id : null);
    if (prevState.voipEnabled !== this.state.voipEnabled
      || rid(prevState.ramal) !== rid(this.state.ramal)
      || (prevState.extensions || []).length !== (this.state.extensions || []).length) {
      this._publishVoipState();
    }
  }

  _publishVoipState = () => {
    const { voipEnabled, ramal, extensions } = this.state;
    const st = {
      enabled: !!voipEnabled,
      hasRamal: (extensions || []).length > 0,
      ramal: ramal ? { id: ramal.id, ext: ramal.ext, name: ramal.name, provider: ramal.provider } : null,
    };
    window.__mexxVoip = st;
    window.dispatchEvent(new CustomEvent('voip:state', { detail: st }));
  };

  // Outro agente do ramal resolveu a MESMA ligação recebida → fecha o painel aqui.
  _onCallHandled = (e) => {
    const p = (e && e.detail) || {};
    const s = this.state.call;
    if (s && s.direction === 'inbound' && s.state === 'received' && String(s.id) === String(p.callId)) {
      this.dismissReceived();
    }
  };

  // Carrega configs ATIVAS (gate) + ramais do agente logado (real).
  // Sem async/await de propósito: o babel 7.12.3 do react-scripts 4 quebra
  // com class field async arrow ("Couldn't find a Program"). Promise chain resolve.
  loadSetup = () => {
    voipService.getConfigs()
      .then((cfgRes) => {
        const configs = ((cfgRes && cfgRes.data) || []).map(voipService.normalizeConfig).filter((c) => c.isActive);
        if (!configs.length) { if (this._mounted) this.setState({ voipEnabled: false, loadingSetup: false }); return null; }

        const userId = this.props.userSession && this.props.userSession.user && this.props.userSession.user.id;
        return Promise.all(configs.map((c) =>
          voipService.getExtensions(c.id).then((r) => ((r && r.data) || []).map(voipService.normalizeExtension))
        )).then((lists) => {
          const all = [].concat.apply([], lists);
          const mine = all
            .filter((e) => String(e.userId) === String(userId) && e.isActive)
            .map((e) => ({ id: e.id, ext: e.extension, name: e.userName || '', configId: e.voipProviderConfigId, provider: e.providerName || '' }));
          if (this._mounted) this.setState((s) => {
            const keep = s.ramal && mine.find((m) => m.id === s.ramal.id);
            const sel = keep || mine[0] || null;
            return { voipEnabled: true, loadingSetup: false, extensions: mine, ramal: sel, draftRamal: sel };
          });
        });
      })
      .catch(() => { if (this._mounted) this.setState({ voipEnabled: false, loadingSetup: false }); });
  };

  _onExternalDial = (e) => {
    const { number, contact, calledId } = (e && e.detail) || {};
    this.setState({ open: true }, () => this.startOutbound(number || this.state.dialNumber, contact, calledId));
  };

  _clearSeq = () => {
    this._seq.forEach((t) => clearTimeout(t));
    this._seq = [];
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
  };
  _push = (fn, ms) => { this._seq.push(setTimeout(fn, ms)); };
  _startTimer = () => {
    this._timer = setInterval(() => this.setState((s) => (s.call ? { call: { ...s.call, seconds: s.call.seconds + 1 } } : null)), 1000);
  };

  startOutbound = (number, contact, calledId) => {
    this._clearSeq();
    const ramal = this.state.ramal;

    // CLICK-TO-CALL = REAL: origina via API (toca o softphone do agente) + persiste.
    if (this.state.modality === 'click_to_call') {
      if (!ramal) { this.setState({ open: true, call: { direction: 'outbound', state: 'ctc_error', number, contact, error: this.fmt('voip.sp.noRamal', 'Você não tem um ramal configurado.') } }); return; }
      const dest = toDialNumber(number);
      this.setState({ presence: 'busy', ctxOpen: false, call: { direction: 'outbound', state: 'ctc_calling', number, contact, ramalExt: ramal.ext, calledId: calledId || null } });
      voipService.callFromExtension(ramal.id, dest, calledId)
        .then((res) => {
          if (!this._mounted) return;
          if (res && res.success) this.setState((s) => ({ call: s.call ? { ...s.call, state: 'ctc_initiated', callId: res.data && res.data.id } : null }));
          else this.setState((s) => ({ call: s.call ? { ...s.call, state: 'ctc_error', error: (res && res.errors && res.errors[0]) || this.fmt('voip.sp.callFail', 'Falha ao originar a chamada.') } : null }));
        })
        .catch(() => { if (this._mounted) this.setState((s) => ({ call: s.call ? { ...s.call, state: 'ctc_error', error: this.fmt('voip.sp.callFail', 'Falha ao originar a chamada.') } : null, presence: 'available' })); });
      return;
    }

    // WEBRTC = preview simulado (engine SIP é fatia futura).
    this.setState({ presence: 'busy', ctxOpen: false, call: { direction: 'outbound', state: 'dialing_agent', number, contact, seconds: 0, muted: false, held: false, linkedCalledId: null } });
    this._push(() => { this.setState((s) => ({ call: { ...s.call, state: 'in_call' } })); this._startTimer(); }, 1800);
  };

  // Troca de aba do discador; Recentes carrega sob demanda (real).
  changeTab = (k) => {
    this.setState({ dialTab: k });
    if (k === 'recents' && !this.state.recentsLoaded) this.refreshRecents();
  };

  // Recarrega só a lista de recentes, sem sair da aba.
  refreshRecents = () => {
    this.setState({ loadingRecents: true });
    voipService.getRecentCalls(20)
      .then((res) => { if (this._mounted) this.setState({ recents: ((res && res.data) || []).map(voipService.normalizeCall), recentsLoaded: true }); })
      .catch(() => {})
      .then(() => { if (this._mounted) this.setState({ loadingRecents: false }); });
  };

  // Busca de contatos (tb_users + tb_2talk_customer) ao clicar na lupa / Enter.
  doSearchContacts = () => {
    const term = this.state.contactQuery.trim();
    if (!term) { this.setState({ contactResults: [], contactSearched: false }); return; }
    this.setState({ loadingContacts: true, contactSearched: true });
    voipService.searchContacts(term)
      .then((res) => { if (this._mounted) this.setState({ contactResults: ((res && res.data) || []).map(voipService.normalizeContact) }); })
      .catch(() => { if (this._mounted) this.setState({ contactResults: [] }); })
      .then(() => { if (this._mounted) this.setState({ loadingContacts: false }); });
  };

  // Ligação RECEBIDA é detectada pós-chamada (o history da Nvoip só traz concluídas) — não é ring ao vivo.
  // Chega pelo SignalR "IncomingCall" (App.js → evento 'voip:incoming-call').
  _onIncomingCall = (e) => {
    const p = (e && e.detail) || {};
    console.log('[VOIP] widget _onIncomingCall:', p, '| voipEnabled=', this.state.voipEnabled);
    if (String(p.type) !== 'incoming_call') return;
    const contact = p.contactName ? { name: p.contactName, org: p.organizationName || '', matched: true } : null;
    const call = {
      direction: 'inbound', state: 'received', id: p.callId || null, number: p.fromNumber || '',
      contact, matchedUserId: p.matchedUserId || null,
      when: this.fmt('voip.sp.justNow', 'agora há pouco'),
      duration: p.durationSeconds || null, recordingReady: Boolean(p.recordingReady), linkedCalledId: null,
    };
    this.setState({ open: true, call, linkCandidates: [], loadingCandidates: Boolean(p.matchedUserId && p.callId), linking: false });
    if (p.matchedUserId && p.callId) this._loadLinkCandidates(p.callId);
  };

  // Chamados abertos do contato casado (fallback: lista vazia → só "criar novo").
  _loadLinkCandidates = (callId) => {
    voipService.getLinkCandidates(callId)
      .then((res) => { if (this._mounted) this.setState({ linkCandidates: (res && res.data) || [], loadingCandidates: false }); })
      .catch(() => { if (this._mounted) this.setState({ linkCandidates: [], loadingCandidates: false }); });
  };

  dismissReceived = () => { this._clearSeq(); this.setState({ call: null, ctxOpen: false, linkCandidates: [], linking: false }); };
  hangup = () => {
    this._clearSeq();
    this.setState((s) => (s.call ? { call: { ...s.call, state: 'ended' } } : null));
    this._push(() => this.setState({ call: null, ctxOpen: false, presence: 'available' }), 1300);
  };

  // Vincula a ligação recebida a um chamado existente (backend anexa a gravação retroativa).
  linkCalled = (id) => {
    if (id === 'novo') return this._quickCreate();
    const callId = this.state.call && this.state.call.id;
    if (!callId) { this.setState((s) => ({ call: { ...s.call, linkedCalledId: id } })); return; } // DEV mock
    this.setState({ linking: true });
    voipService.associateCalled(callId, id)
      .then((res) => {
        if (!this._mounted) return;
        if (res && res.success) {
          this.setState((s) => ({ call: { ...s.call, linkedCalledId: id }, linking: false }));
          window.dispatchEvent(new CustomEvent('voip:refresh-calls', { detail: { calledId: id } }));
        } else { this.setState({ linking: false }); }
      })
      .catch(() => { if (this._mounted) this.setState({ linking: false }); });
  };

  // Cria um chamado mínimo a partir da ligação e vincula (quick-create inline).
  _quickCreate = () => {
    const callId = this.state.call && this.state.call.id;
    if (!callId) { this.setState((s) => ({ call: { ...s.call, linkedCalledId: 'novo' } })); return; } // DEV mock
    this.setState({ linking: true });
    voipService.quickCreateCalled(callId)
      .then((res) => {
        if (!this._mounted) return;
        if (res && res.success) {
          const d = res.data || {};
          const newId = d.calledid ?? d.calledId ?? 'novo';
          this.setState((s) => ({ call: { ...s.call, linkedCalledId: newId }, linking: false }));
          window.dispatchEvent(new CustomEvent('voip:refresh-calls', { detail: { calledId: newId } }));
        } else { this.setState({ linking: false }); }
      })
      .catch(() => { if (this._mounted) this.setState({ linking: false }); });
  };

  // Conclui a chamada click-to-call e avisa a aba do chamado pra recarregar o histórico.
  finishCall = () => {
    const calledId = this.state.call && this.state.call.calledId;
    window.dispatchEvent(new CustomEvent('voip:refresh-calls', { detail: { calledId } }));
    this.setState({ call: null, presence: 'available' });
  };

  pressKey = (k) => this.setState((s) => ({ dialNumber: s.dialNumber + k }));
  backspace = () => this.setState((s) => ({ dialNumber: s.dialNumber.slice(0, -1) }));

  openSettings = (e) => { this.loadSetup(); this.setState({ settingsAnchor: e.currentTarget, draftRamal: this.state.ramal, draftModality: this.state.modality }); };
  applySettings = () => this.setState((s) => ({ ramal: s.draftRamal, modality: s.draftModality, settingsAnchor: null }));

  fmt = (id, def) => this.props.intl.formatMessage({ id, defaultMessage: def });

  skelRows = (n) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
      {Array.from({ length: n }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1 }}><Skeleton variant="text" width="55%" height={14} /><Skeleton variant="text" width="78%" height={11} /></Box>
        </Box>
      ))}
    </Box>
  );

  renderContacts = () => {
    const { contactResults, loadingContacts, contactSearched } = this.state;
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField fullWidth size="small" placeholder={this.fmt('voip.sp.searchContact', 'Buscar contato (nome ou número)')}
            value={this.state.contactQuery} onChange={(e) => this.setState({ contactQuery: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') this.doSearchContacts(); }} sx={{ flex: 1 }} />
          <Tooltip title={this.fmt('voip.sp.search', 'Buscar')}>
            <IconButton onClick={this.doSearchContacts} sx={{ width: 40, height: 40, flexShrink: 0, background: vt.greenGrad, '&:hover': { background: vt.greenGradHover }, '& svg': { color: '#fff !important' }, '& path': { fill: '#fff !important' } }}>
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ maxHeight: 290, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {loadingContacts ? this.skelRows(5)
            : !contactSearched ? <Typography sx={{ fontSize: 12.5, color: vt.textSoft, textAlign: 'center', py: 3 }}>{this.fmt('voip.sp.searchHint', 'Digite um nome ou número e busque.')}</Typography>
              : contactResults.length === 0 ? <Typography sx={{ fontSize: 12.5, color: vt.textSoft, textAlign: 'center', py: 3 }}>{this.fmt('voip.sp.noContact', 'Nenhum contato encontrado')}</Typography>
                : contactResults.map((c) => {
                  const valid = c.phoneValid && isValidPhone(c.phone);
                  return (
                    <Box key={`${c.source}-${c.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, border: '1px solid transparent', '&:hover': { bgcolor: vt.surfaceAlt, borderColor: vt.border } }}>
                      <Avatar sx={{ width: 34, height: 34, bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', fontWeight: 700, fontSize: 14 }}>{String(c.name || '?').charAt(0).toUpperCase()}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: vt.text }} noWrap>{c.name}</Typography>
                        {valid ? (
                          <Typography sx={{ fontSize: 11, color: vt.textSoft }} noWrap>{c.phone} · {c.source === 'user' ? this.fmt('voip.sp.srcClient', 'Cliente') : this.fmt('voip.sp.srcContact', 'Contato')}</Typography>
                        ) : (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                            <WarningAmberIcon sx={{ fontSize: 12, color: vt.amber }} />
                            <Typography sx={{ fontSize: 11, color: vt.amber, fontWeight: 600 }}>{c.phone ? this.fmt('voip.sp.invalidPhone', 'Telefone inválido') : this.fmt('voip.sp.noPhone', 'Sem telefone')}</Typography>
                          </Box>
                        )}
                      </Box>
                      {valid && (
                        <Tooltip title={this.fmt('voip.sp.dial', 'Ligar')}>
                          <IconButton size="small" onClick={() => this.startOutbound(c.phone, { name: c.name, org: '' })} sx={{ bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}><PhoneIcon sx={{ fontSize: 17 }} /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
        </Box>
      </Box>
    );
  };

  renderRecents = () => {
    const { recents, loadingRecents } = this.state;
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, px: 0.5 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: vt.muted, letterSpacing: 0.5 }}>{this.fmt('voip.sp.tab.recents', 'Recentes')}</Typography>
          <Tooltip title={this.fmt('voip.sp.refresh', 'Atualizar')}>
            <span>
              <IconButton size="small" onClick={this.refreshRecents} disabled={loadingRecents} sx={{ color: vt.textSoft, '&:hover': { color: vt.green, bgcolor: vt.greenSoft } }}>
                <RefreshIcon sx={{ fontSize: 17, animation: loadingRecents ? 'voipSpin 0.8s linear infinite' : 'none' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {loadingRecents ? this.skelRows(5)
          : recents.length === 0 ? <Typography sx={{ fontSize: 12.5, color: vt.textSoft, textAlign: 'center', py: 3 }}>{this.fmt('voip.sp.noRecents', 'Sem ligações recentes')}</Typography>
            : recents.map((c) => {
              const num = callNumber(c);
              const failed = c.status === 'missed' || c.status === 'failed';
              return (
                <Box key={c.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, '&:hover': { bgcolor: vt.surfaceAlt } }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: c.direction === 'inbound' ? vt.blueSoft : vt.greenSoft, color: c.direction === 'inbound' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }}>
                    {c.direction === 'inbound' ? <CallReceivedIcon sx={{ fontSize: 16 }} /> : <CallMadeIcon sx={{ fontSize: 16 }} />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: failed ? vt.red : vt.text, minWidth: 0 }} noWrap>{num}</Typography>
                      {c.providerName ? <Box component="span" sx={{ flexShrink: 0, px: 0.6, py: '1px', borderRadius: 0.75, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 9, fontWeight: 700, color: vt.textSoft, lineHeight: 1.4 }}>{c.providerName}</Box> : null}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: vt.textSoft }} noWrap>{c.direction === 'inbound' ? this.fmt('voip.call.hist.inbound', 'Recebida') : this.fmt('voip.call.hist.outbound', 'Realizada')}{c.startedAt ? ` · ${fmtWhen(c.startedAt)}` : ''}</Typography>
                  </Box>
                  <Tooltip title={this.fmt('voip.sp.dial', 'Ligar')}>
                    <IconButton size="small" onClick={() => this.startOutbound(num, null)} sx={{ bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}><PhoneIcon sx={{ fontSize: 17 }} /></IconButton>
                  </Tooltip>
                </Box>
              );
            })}
        </Box>
      </Box>
    );
  };

  render() {
    const { userSession } = this.props;
    const user = userSession && userSession.user;
    if (!user) return null;
    if ((user.userrole || []).some((r) => r.role && r.role.roletype == 3)) return null;
    // Gate real: só renderiza com provider VOIP habilitado (config ativa) e ramal resolvido.
    if (this.state.voipEnabled !== true) return null;

    const { open, presence, modality, ramal, call, dialNumber, dialTab } = this.state;
    const mode = MODES[modality];
    const presColor = presenceColors[presence];
    const inbound = call && call.direction === 'inbound';
    const receivedIn = inbound && call.state === 'received';
    const isWebrtc = modality === 'webrtc';

    // ---- FAB minimizado (centro vertical, alinhado à direita) ----
    if (!open) {
      return (
        <Box sx={{ position: 'fixed', right: 18, top: '50%', transform: 'translateY(-50%)', zIndex: 13000 }}>
          <Box sx={{ position: 'relative' }}>
            <IconButton onClick={() => { this.loadSetup(); this.setState({ open: true }); }}
              sx={{ width: 60, height: 60, background: call ? vt.slateBlue : vt.greenGrad, color: '#fff',
                boxShadow: vt.shadowPop, '&:hover': { background: call ? vt.slateBlue : vt.greenGradHover },
                animation: call ? 'voipRing 1.1s infinite' : 'none' }}>
              {call ? <PhoneInTalkIcon /> : <PhoneIcon />}
            </IconButton>
            <Box sx={{ position: 'absolute', right: 2, bottom: 2, width: 15, height: 15, borderRadius: '50%', bgcolor: presColor, border: '3px solid #fff' }} />
          </Box>
          <style>{voipKeyframes}</style>
        </Box>
      );
    }

    return (
      <Paper elevation={0} sx={{ position: 'fixed', right: 18, top: '50%', transform: 'translateY(-50%)', zIndex: 13000,
        width: 360, maxHeight: '92vh', overflowY: 'auto', borderRadius: `${vt.radius}px`, userSelect: 'none',
        boxShadow: vt.shadowPop, border: `1px solid ${vt.border}`, bgcolor: vt.surface }}>

        {/* ===== HEADER (presença + ramal + provider) ===== */}
        <Box sx={{ background: receivedIn ? vt.slateBlue : vt.slate, color: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, position: 'sticky', top: 0, zIndex: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar sx={{ width: 38, height: 38, bgcolor: 'rgba(255,255,255,.14)', color: '#fff', fontWeight: 700, fontSize: 15 }}>
              {String(user.name || 'A').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: '50%', bgcolor: presColor, border: '2px solid #1A2332' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.15, color: '#fff' }} noWrap>{user.name}</Typography>
            <Box onClick={(e) => this.setState({ presenceAnchor: e.currentTarget })}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mt: 0.2,
                px: 0.75, py: 0.1, borderRadius: 999, bgcolor: 'rgba(255,255,255,.08)', '&:hover': { bgcolor: 'rgba(255,255,255,.16)' } }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: presColor }} />
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.85)' }}>{this.fmt(PRESENCE[presence].key, PRESENCE[presence].def)}</Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.8 }} />
            </Box>
          </Box>
          <Tooltip title={this.fmt('voip.sp.settings', 'Configurações de telefonia')}>
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,.75)', '&:hover': { color: '#fff' } }} onClick={this.openSettings}>
              <SettingsIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={this.fmt('voip.sp.minimize', 'Minimizar')}>
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,.75)', '&:hover': { color: '#fff' } }} onClick={() => this.setState({ open: false, ramalMenuAnchor: null, settingsAnchor: null, presenceAnchor: null })}>
              <CloseIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* provider line: ramal ativo + modo de chamada */}
        <Box sx={{ px: 2, py: 0.85, bgcolor: vt.surfaceAlt, borderBottom: `1px solid ${vt.border}`, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <HeadsetMicIcon sx={{ fontSize: 14, color: vt.muted }} />
          {this.state.extensions.length > 1 ? (
            <Box onClick={(e) => this.setState({ ramalMenuAnchor: e.currentTarget })} sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': { bgcolor: 'rgba(0,0,0,.04)' } }}>
              <Typography sx={{ fontSize: 11.5, color: vt.textSoft, fontWeight: 600, minWidth: 0 }} noWrap>{ramal ? `${this.fmt('voip.sp.ext', 'Ramal')} ${ramal.ext}${ramal.name ? ` · ${ramal.name}` : ''}` : this.fmt('voip.sp.noRamalShort', 'Sem ramal configurado')}</Typography>
              {ramal && ramal.provider ? <Box sx={{ flexShrink: 0, px: 0.7, py: 0.1, borderRadius: 1, bgcolor: '#fff', border: `1px solid ${vt.border}`, fontSize: 9.5, fontWeight: 700, color: vt.textSoft }}>{ramal.provider}</Box> : null}
              <Box component="span" sx={{ flexShrink: 0, fontSize: 12, color: vt.muted, lineHeight: 1 }}>▾</Box>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 11.5, color: vt.textSoft, fontWeight: 600, flex: 1 }} noWrap>{ramal ? `${this.fmt('voip.sp.ext', 'Ramal')} ${ramal.ext}${ramal.name ? ` · ${ramal.name}` : ''}` : this.fmt('voip.sp.noRamalShort', 'Sem ramal configurado')}</Typography>
              {ramal && ramal.provider ? <Box sx={{ flexShrink: 0, px: 0.75, py: 0.15, borderRadius: 1, bgcolor: '#fff', border: `1px solid ${vt.border}`, fontSize: 10, fontWeight: 700, color: vt.textSoft }}>{ramal.provider}</Box> : null}
            </>
          )}
          <Menu anchorEl={this.state.ramalMenuAnchor} open={Boolean(this.state.ramalMenuAnchor)} onClose={() => this.setState({ ramalMenuAnchor: null })}
            disableScrollLock sx={{ zIndex: 15000 }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { minWidth: 220, borderRadius: 2, boxShadow: vt.shadowMd } }}>
            {this.state.extensions.map((x) => (
              <MenuItem key={x.id} selected={ramal && ramal.id === x.id} onClick={() => this.setState({ ramal: x, draftRamal: x, ramalMenuAnchor: null })} sx={{ fontSize: 12.5, gap: 1 }}>
                <span style={{ flex: 1 }}>{x.ext}{x.name ? ` · ${x.name}` : ''}</span>
                {x.provider ? <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 10, fontWeight: 700, color: vt.textSoft }}>{x.provider}</Box> : null}
              </MenuItem>
            ))}
          </Menu>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.2, borderRadius: 999, bgcolor: modality === 'webrtc' ? vt.blueSoft : vt.greenSoft }}>
            <mode.Icon sx={{ fontSize: 12, color: modality === 'webrtc' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }} />
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: modality === 'webrtc' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }}>{this.fmt(mode.key, mode.def)}</Typography>
          </Box>
        </Box>

        {/* menu de presença (z-index acima do widget!) */}
        <Menu anchorEl={this.state.presenceAnchor} open={Boolean(this.state.presenceAnchor)} onClose={() => this.setState({ presenceAnchor: null })}
          disableScrollLock sx={{ zIndex: 14000 }} PaperProps={{ sx: { borderRadius: 2, boxShadow: vt.shadowMd, minWidth: 180 } }}>
          {Object.keys(PRESENCE).map((k) => (
            <MenuItem key={k} selected={presence === k} onClick={() => this.setState({ presence: k, presenceAnchor: null })} sx={{ fontSize: 13.5, gap: 1 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: presenceColors[k] }} />
              {this.fmt(PRESENCE[k].key, PRESENCE[k].def)}
              {presence === k && <CheckRoundedIcon sx={{ fontSize: 16, color: vt.green, ml: 'auto' }} />}
            </MenuItem>
          ))}
        </Menu>

        {/* popover de configurações: meu ramal (autocomplete) + modo de chamada + Aplicar */}
        <Popover anchorEl={this.state.settingsAnchor} open={Boolean(this.state.settingsAnchor)} onClose={() => this.setState({ settingsAnchor: null })}
          disableScrollLock sx={{ zIndex: 14000 }} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 322, borderRadius: 3, boxShadow: vt.shadowPop, p: 2, mt: 0.5, overflow: 'visible' } }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: vt.text, mb: 1.5 }}>{this.fmt('voip.sp.settings', 'Configurações de telefonia')}</Typography>

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, mb: 0.75 }}>{this.fmt('voip.sp.myExtension', 'MEU RAMAL')}</Typography>
          {this.state.extensions.length === 0 ? (
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}` }}>
              <Typography sx={{ fontSize: 12, color: vt.textSoft }}>{this.fmt('voip.sp.noRamal', 'Você não tem um ramal configurado.')}</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={this.state.extensions} value={this.state.draftRamal} disableClearable
              disablePortal
              getOptionLabel={(o) => `${o.ext}${o.name ? ` · ${o.name}` : ''}${o.provider ? ` (${o.provider})` : ''}`} isOptionEqualToValue={(o, v) => o.id === (v && v.id)}
              onChange={(e, v) => v && this.setState({ draftRamal: v })}
              componentsProps={{ popper: { sx: { zIndex: 15000 } } }}
              renderOption={(p, o) => (
                <Box component="li" {...p} key={o.id} sx={{ fontSize: 13.5, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: 11, fontWeight: 700, color: vt.textSoft }}>{String(o.ext).slice(-2)}</Box>
                  <span style={{ flex: 1 }}>{o.ext}{o.name ? ` · ${o.name}` : ''}</span>
                  {o.provider ? <Box sx={{ ml: 1, px: 0.75, py: 0.15, borderRadius: 1, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 10.5, fontWeight: 700, color: vt.textSoft }}>{o.provider}</Box> : null}
                </Box>
              )}
              renderInput={(params) => <TextField {...params} size="small" placeholder={this.fmt('voip.sp.searchExt', 'Buscar ramal')}
                InputProps={{ ...params.InputProps, startAdornment: <DialpadIcon sx={{ fontSize: 17, color: vt.muted, ml: 0.5, mr: 0.5 }} /> }} />}
            />
          )}

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, mt: 2, mb: 0.75 }}>{this.fmt('voip.sp.callMode', 'MODO DE CHAMADA')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.keys(MODES).map((k) => {
              const m = MODES[k]; const sel = this.state.draftModality === k; const accent = k === 'webrtc' ? vt.blue : 'var(--mexx-green-dark, #558B2F)';
              return (
                <Box key={k} onClick={() => this.setState({ draftModality: k })}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25, borderRadius: 2.5, cursor: 'pointer',
                    border: `1.5px solid ${sel ? accent : vt.border}`, bgcolor: sel ? (k === 'webrtc' ? vt.blueSoft : vt.greenSoft) : vt.surface, transition: 'all .15s' }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: sel ? accent : vt.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <m.Icon sx={{ fontSize: 19, color: sel ? '#fff' : vt.textSoft }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: vt.text }}>{this.fmt(m.key, m.def)}</Typography>
                      {sel && <CheckRoundedIcon sx={{ fontSize: 16, color: accent }} />}
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: vt.textSoft, lineHeight: 1.35 }}>{this.fmt(m.descKey, m.desc)}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button fullWidth onClick={() => this.setState({ settingsAnchor: null })} sx={{ textTransform: 'none', color: vt.textSoft, fontWeight: 600 }}>{this.fmt('voip.sp.cancel', 'Cancelar')}</Button>
            <Button fullWidth variant="contained" onClick={this.applySettings}
              sx={{ background: vt.greenGrad, color: '#fff !important', fontWeight: 700, textTransform: 'none', py: 0.9, borderRadius: 2, boxShadow: '0 6px 16px rgba(85,139,47,.28)', '&:hover': { background: vt.greenGradHover }, '& *': { color: '#fff !important' } }}>
              <span style={{ color: '#fff' }}>{this.fmt('voip.sp.apply', 'Aplicar')}</span>
            </Button>
          </Box>
        </Popover>

        {/* ===== CORPO ===== */}
        <Box sx={{ p: 2.25 }}>
          {/* ---- INBOUND recebida (PÓS-CHAMADA — não é ring ao vivo) ---- */}
          {receivedIn && (
            <Box>
              {/* cabeçalho: contato identificado */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar sx={{ width: 46, height: 46, bgcolor: vt.blue, color: '#fff', fontSize: 18, fontWeight: 700 }}>
                    {call.contact ? String(call.contact.name).charAt(0) : <CallReceivedIcon />}
                  </Avatar>
                  <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 20, height: 20, borderRadius: '50%', bgcolor: vt.blue, border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CallReceivedIcon sx={{ fontSize: 11, color: '#fff' }} />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11, color: vt.blue, fontWeight: 700, letterSpacing: 0.5 }}>{this.fmt('voip.sp.received', 'LIGAÇÃO RECEBIDA')}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: vt.text }} noWrap>{call.contact ? call.contact.name : call.number}</Typography>
                  <Typography sx={{ fontSize: 12, color: vt.textSoft }} noWrap>{call.contact ? `${call.number} · ${call.contact.org}` : this.fmt('voip.sp.unknown', 'Não identificado')}</Typography>
                </Box>
              </Box>

              {/* meta da chamada */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.3, borderRadius: 999, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 10.5, color: vt.textSoft, fontWeight: 600 }}>
                  <HeadsetMicIcon sx={{ fontSize: 13, color: vt.muted }} />{this.fmt('voip.sp.answeredSoftphone', 'Atendida no seu softphone')}
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 0.9, py: 0.3, borderRadius: 999, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 10.5, color: vt.textSoft, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtClock(call.duration)}
                </Box>
                {call.recordingReady && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.3, borderRadius: 999, bgcolor: vt.greenSoft, border: `1px solid ${vt.green}`, fontSize: 10.5, color: vt.green, fontWeight: 700 }}>
                    <MicIcon sx={{ fontSize: 13, color: vt.green }} />{this.fmt('voip.sp.recAvailable', 'Gravação')}
                  </Box>
                )}
              </Box>
              <Typography sx={{ fontSize: 11, color: vt.muted, mb: 1.5 }}>{call.when}</Typography>

              {/* vincular / criar chamado */}
              <Divider sx={{ mb: 1.5 }} />
              {call.linkedCalledId ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: 2, bgcolor: vt.greenSoft, border: `1px solid ${vt.green}` }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: vt.greenGrad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: vt.text }}>
                        {call.linkedCalledId === 'novo' ? this.fmt('voip.sp.linkedNew', 'Novo chamado criado e vinculado') : `${this.fmt('voip.sp.linked', 'Vinculada ao chamado')} #${call.linkedCalledId}`}
                      </Typography>
                      {call.recordingReady && <Typography sx={{ fontSize: 11, color: vt.textSoft }}>{this.fmt('voip.sp.recAttached', 'Gravação anexada ao chamado')}</Typography>}
                    </Box>
                  </Box>
                  <Button fullWidth variant="contained" onClick={this.dismissReceived} sx={{ ...primaryBtnSx, color: '#fff !important', mt: 1.5, py: 1, borderRadius: 2.5 }}>{this.fmt('voip.sp.done', 'Concluir')}</Button>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: vt.muted, letterSpacing: 0.5, mb: 1 }}>{this.fmt('voip.sp.linkTitle', 'VINCULAR ESTA LIGAÇÃO')}</Typography>
                  {this.state.loadingCandidates ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.75 }}><CircularProgress size={22} sx={{ color: vt.green }} /></Box>
                  ) : this.state.linkCandidates.length > 0 ? (
                    this.state.linkCandidates.map((c) => (
                      <Box key={c.id} onClick={() => { if (!this.state.linking) this.linkCalled(c.id); }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1.25, borderRadius: 2, border: `1px solid ${vt.border}`, cursor: this.state.linking ? 'default' : 'pointer', opacity: this.state.linking ? 0.6 : 1,
                          transition: 'all .15s', '&:hover': this.state.linking ? {} : { borderColor: vt.green, bgcolor: vt.greenSoft, transform: 'translateY(-1px)' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: vt.text }} noWrap>#{c.id} · {c.title}</Typography>
                          <Typography sx={{ fontSize: 11, color: vt.textSoft }}>{c.status}</Typography>
                        </Box>
                        <NorthEastIcon sx={{ fontSize: 16, color: vt.muted }} />
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ fontSize: 12, color: vt.textSoft, mb: 1 }}>{this.fmt('voip.sp.noOpenCalleds', 'Sem chamados abertos deste contato. Crie um novo abaixo.')}</Typography>
                  )}
                  <Button fullWidth disabled={this.state.linking} startIcon={this.state.linking ? <CircularProgress size={15} sx={{ color: vt.green }} /> : <AddIcon />} onClick={() => this.linkCalled('novo')}
                    sx={{ mt: 0.5, color: vt.green, fontWeight: 700, textTransform: 'none', border: `1px dashed ${vt.green}`, borderRadius: 2, py: 0.9, '&:hover': { bgcolor: vt.greenSoft }, '&.Mui-disabled': { opacity: 0.6 } }}>
                    {this.fmt('voip.sp.createCalled', 'Criar novo chamado')}
                  </Button>
                  <Button fullWidth disabled={this.state.linking} onClick={this.dismissReceived} sx={{ mt: 1, color: vt.textSoft, fontWeight: 600, textTransform: 'none' }}>{this.fmt('voip.sp.dismiss', 'Dispensar')}</Button>
                </>
              )}
            </Box>
          )}

          {/* ---- CHAMADA ATIVA ---- */}
          {/* CLICK-TO-CALL REAL — confirmação honesta (sem timer falso) */}
          {call && String(call.state).startsWith('ctc') && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              {call.state === 'ctc_calling' && (
                <>
                  <CircularProgress size={44} sx={{ color: vt.green, mb: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: vt.text }}>{this.fmt('voip.sp.ctc.starting', 'Iniciando ligação…')}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: vt.textSoft, mt: 0.5 }}>{call.contact ? call.contact.name : call.number}</Typography>
                </>
              )}
              {call.state === 'ctc_initiated' && (
                <>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', background: vt.greenGrad, boxShadow: vt.shadowMd }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 40, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 17, color: vt.text }}>{this.fmt('voip.sp.ctc.ringing', 'Ligando pelo seu softphone')}</Typography>
                  <Typography sx={{ fontSize: 13, color: vt.textSoft, mt: 0.5 }}>{this.fmt('voip.sp.ext', 'Ramal')} {call.ramalExt} → {call.contact ? call.contact.name : call.number}</Typography>
                  <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}` }}>
                    <Typography sx={{ fontSize: 11.5, color: vt.textSoft, lineHeight: 1.4 }}>{this.fmt('voip.sp.ctc.answerSoftphone', 'Atenda seu telefone para falar com o cliente. A ligação foi registrada no histórico.')}</Typography>
                  </Box>
                  <Button fullWidth variant="contained" onClick={this.finishCall} sx={{ ...primaryBtnSx, color: '#fff !important', mt: 1.75, py: 1, borderRadius: 2.5 }}>{this.fmt('voip.sp.done', 'Concluir')}</Button>
                </>
              )}
              {call.state === 'ctc_error' && (
                <>
                  <Box sx={{ width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', background: vt.redGrad, boxShadow: vt.shadowMd }}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: vt.text }}>{this.fmt('voip.sp.ctc.failed', 'Não foi possível ligar')}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: vt.textSoft, mt: 0.5 }}>{call.error}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.75 }}>
                    <Button fullWidth onClick={() => this.setState({ call: null, presence: 'available' })} sx={{ textTransform: 'none', color: vt.textSoft, fontWeight: 600 }}>{this.fmt('voip.sp.close', 'Fechar')}</Button>
                    <Button fullWidth variant="contained" onClick={() => this.startOutbound(call.number, call.contact)} sx={{ ...primaryBtnSx, color: '#fff !important', py: 0.9, borderRadius: 2 }}>{this.fmt('voip.sp.retry', 'Tentar de novo')}</Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* WEBRTC (preview simulado) */}
          {call && !receivedIn && !String(call.state).startsWith('ctc') && (
            <Box sx={{ textAlign: 'center', py: 0.5 }}>
              <Box sx={{ width: 78, height: 78, borderRadius: '50%', mx: 'auto', mb: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: call.state === 'in_call' ? vt.greenGrad : vt.slateBlue, color: '#fff', boxShadow: vt.shadowMd,
                animation: call.state !== 'in_call' && call.state !== 'ended' ? 'voipPulse 1.3s infinite' : 'none' }}>
                <PhoneInTalkIcon sx={{ fontSize: 36, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 17, color: vt.text }}>{call.contact ? call.contact.name : call.number}</Typography>
              <Typography sx={{ fontSize: 12.5, color: vt.textSoft, mt: 0.25 }}>
                {call.state === 'dialing_agent'
                  ? (isWebrtc ? this.fmt('voip.sp.state.calling', 'Chamando…') : `${this.fmt('voip.sp.state.dialing_agent', 'Tocando seu ramal')} ${ramal ? ramal.ext : ''}…`)
                  : call.state === 'ringing_client' ? this.fmt('voip.sp.state.ringing_client', 'Conectando ao cliente…')
                  : call.state === 'in_call' ? this.fmt('voip.sp.state.in_call', 'Em ligação')
                  : this.fmt('voip.sp.state.ended', 'Chamada encerrada')}
              </Typography>
              {call.state === 'in_call' && (
                <Typography sx={{ fontWeight: 800, fontSize: 26, color: vt.text, mt: 0.5, fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}>{fmtClock(call.seconds)}</Typography>
              )}
              {call.state === 'in_call' && (isWebrtc ? (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 999, bgcolor: vt.blueSoft }}>
                    <LanguageIcon sx={{ fontSize: 12, color: vt.blue }} />
                    <Typography sx={{ fontSize: 10.5, color: vt.blue, fontWeight: 700 }}>{this.fmt('voip.sp.connBrowser', 'Conectado pelo navegador')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 22 }}>
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                      <Box key={i} sx={{ width: 4, borderRadius: 2, height: '100%', transformOrigin: 'bottom',
                        bgcolor: call.muted ? vt.muted : vt.green, transform: call.muted ? 'scaleY(.18)' : 'none',
                        animation: call.muted ? 'none' : `voipBar .9s ${i * 0.12}s ease-in-out infinite` }} />
                    ))}
                  </Box>
                  {call.muted && <Typography sx={{ fontSize: 10.5, color: vt.red, fontWeight: 600 }}>{this.fmt('voip.sp.micMuted', 'Microfone silenciado')}</Typography>}
                </Box>
              ) : (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, px: 1, py: 0.25, borderRadius: 999, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}` }}>
                  <HeadsetMicIcon sx={{ fontSize: 12, color: vt.muted }} />
                  <Typography sx={{ fontSize: 10.5, color: vt.textSoft, fontWeight: 500 }}>{this.fmt('voip.sp.audioExternal', 'Áudio no softphone')} · {this.fmt('voip.sp.ext', 'Ramal')} {ramal ? ramal.ext : ''}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2.25 }}>
                {[
                  { icon: call.muted ? <MicOffIcon /> : <MicIcon />, active: call.muted, on: () => this.setState((s) => ({ call: { ...s.call, muted: !s.call.muted } })), tip: isWebrtc ? this.fmt('voip.sp.mute', 'Silenciar') : this.fmt('voip.sp.ctrlExternal', 'Controle no softphone') },
                  { icon: <PauseIcon />, active: call.held, on: () => this.setState((s) => ({ call: { ...s.call, held: !s.call.held } })), tip: isWebrtc ? this.fmt('voip.sp.hold', 'Em espera') : this.fmt('voip.sp.ctrlExternal', 'Controle no softphone') },
                ].map((b, i) => (
                  <Tooltip key={i} title={b.tip}>
                    <span>
                      <IconButton disabled={!isWebrtc || call.state !== 'in_call'} onClick={b.on}
                        sx={{ width: 52, height: 52, bgcolor: b.active ? vt.text : vt.surfaceAlt, color: b.active ? '#fff' : vt.textSoft,
                          border: `1px solid ${b.active ? vt.text : vt.border}`, '&:hover': { bgcolor: b.active ? vt.ink : '#EEF2F6' }, '&.Mui-disabled': { opacity: 0.45 } }}>
                        {b.icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}
                <Tooltip title={this.fmt('voip.sp.hangup', 'Desligar')}>
                  <IconButton onClick={this.hangup} sx={{ width: 52, height: 52, background: vt.redGrad, color: '#fff', boxShadow: '0 6px 16px rgba(220,38,38,.3)', '&:hover': { background: vt.redGrad, filter: 'brightness(1.05)' } }}>
                    <CallEndIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* ---- OCIOSO: Teclado | Contatos | Recentes ---- */}
          {!call && (
            <Box>
              <Box sx={{ display: 'flex', gap: 0.5, p: 0.4, bgcolor: vt.surfaceAlt, borderRadius: 2.5, mb: 2 }}>
                {[
                  { k: 'keypad', icon: <DialpadIcon sx={{ fontSize: 16 }} />, label: this.fmt('voip.sp.tab.keypad', 'Teclado') },
                  { k: 'contacts', icon: <PeopleAltIcon sx={{ fontSize: 16 }} />, label: this.fmt('voip.sp.tab.contacts', 'Contatos') },
                  { k: 'recents', icon: <HistoryIcon sx={{ fontSize: 16 }} />, label: this.fmt('voip.sp.tab.recents', 'Recentes') },
                ].map((t) => {
                  const sel = dialTab === t.k;
                  return (
                    <Box key={t.k} onClick={() => this.changeTab(t.k)}
                      sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4, py: 0.75, borderRadius: 2, cursor: 'pointer',
                        bgcolor: sel ? vt.surface : 'transparent', boxShadow: sel ? vt.shadowSm : 'none', color: sel ? vt.text : vt.textSoft, fontWeight: sel ? 700 : 600, fontSize: 12 }}>
                      {t.icon}{t.label}
                    </Box>
                  );
                })}
              </Box>

              {dialTab === 'keypad' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <PhoneInput country={'br'} value={dialNumber} onChange={(v) => this.setState({ dialNumber: v })}
                        countryCodeEditable={false} enableSearch inputStyle={{ width: '100%', height: 46, fontSize: 17, fontWeight: 600, color: vt.text, border: `1px solid ${vt.border}`, borderRadius: 12 }} specialLabel="" />
                    </Box>
                    <IconButton onClick={this.backspace} sx={{ ml: 0.5, color: vt.muted }}><BackspaceOutlinedIcon /></IconButton>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mb: 2, justifyItems: 'center' }}>
                    {KEYS.map((k) => (
                      <Box key={k.d} onClick={() => this.pressKey(k.d)}
                        sx={{ width: 60, height: 60, borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, transition: 'all .12s',
                          '&:hover': { bgcolor: '#EEF2F6', transform: 'translateY(-1px)' }, '&:active': { transform: 'scale(.95)' } }}>
                        <Typography sx={{ fontSize: 21, fontWeight: 600, color: vt.text, lineHeight: 1 }}>{k.d}</Typography>
                        {k.s && <Typography sx={{ fontSize: 8.5, fontWeight: 700, color: vt.muted, letterSpacing: 1 }}>{k.s}</Typography>}
                      </Box>
                    ))}
                  </Box>
                  <Button fullWidth variant="contained" startIcon={<PhoneIcon />} onClick={() => this.startOutbound(dialNumber || '+55 81 99988-7766', null)}
                    sx={{ ...primaryBtnSx, py: 1.35, borderRadius: 2.5, fontSize: 15 }}>{this.fmt('voip.sp.dial', 'Ligar')}</Button>
                </>
              )}

              {dialTab === 'contacts' && this.renderContacts()}
              {dialTab === 'recents' && this.renderRecents()}
            </Box>
          )}
        </Box>

        <style>{voipKeyframes}</style>
      </Paper>
    );
  }
}

const voipKeyframes = `
@keyframes voipPulse { 0%{transform:scale(1)} 50%{transform:scale(1.06)} 100%{transform:scale(1)} }
@keyframes voipHalo { 0%{transform:scale(.9);opacity:.7} 100%{transform:scale(1.25);opacity:0} }
@keyframes voipRing { 0%{transform:rotate(0)} 25%{transform:rotate(12deg)} 50%{transform:rotate(0)} 75%{transform:rotate(-12deg)} 100%{transform:rotate(0)} }
@keyframes voipBar { 0%,100%{transform:scaleY(.25)} 50%{transform:scaleY(1)} }
@keyframes voipSpin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
`;

const mapStateToProps = (state) => ({ userSession: state.userSession });
export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(VoipSoftphoneWidget)));
